import React from "react";
import classNames from "classnames";

import { ScreenWatcher } from "../../../common/screenWatcher";
import { LayoutWatcher } from "../../../common/layoutWatcher";
import { Props, Option } from "./types";
import styles from "./styles.scss";

function useSelect() {
  const selectElement = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const toggleIsOpen = () => setIsOpen(!isOpen);

  React.useEffect(() => {
    function closeSelect(e: MouseEvent) {
      const selectElContainsTarget =
        selectElement.current &&
        selectElement.current.contains(e.target as Node);

      if (!selectElContainsTarget) {
        setIsOpen(false);
      }
    }

    document.addEventListener("click", closeSelect);

    return () => document.removeEventListener("click", closeSelect);
  }, []);

  return {
    isOpen,
    toggle: toggleIsOpen,
    selectRef: selectElement,
  };
}

function SelectList(props: Props) {
  const { isOpen, toggle, selectRef } = useSelect();
  const hasSelectedOptions = props.selectedOptions.length > 0;
  const swapSelectedItems = (option: Option) =>
    props.options.find(({ id }) => id === option.id) || option;
  const isOptionSelected = (option: Option) =>
    !!props.selectedOptions.find(({ id }) => option.id === id);
  const addItem = (item: Option) => {
    props.selectItem([...props.selectedOptions, item]);
    (props.options.every(({ id }) =>
      [...props.selectedOptions, item].find((option) => option.id === id),
    ) ||
      !props.multiple) &&
      toggle();
  };
  const removeItem = (item: Option) => {
    props.selectItem(props.selectedOptions.filter(({ id }) => id !== item.id));
    !props.multiple && toggle();
  };
  const dispatchChange = (option: Option) => () =>
    isOptionSelected(option) ? removeItem(option) : addItem(option);

  return (
    <div
      ref={selectRef}
      className={classNames(styles.selectList, {
        [styles.multiple]: props.multiple,
        [styles.opened]: isOpen,
        [styles.invalid]: props.isInvalid,
      })}
    >
      <div className={styles.input} onClick={toggle}>
        {hasSelectedOptions ? (
          <ul className={styles.selectedItems}>
            {props.selectedOptions.map(swapSelectedItems).map((option) => (
              <li key={option.id} className={styles.selectedItem}>
                {option.name}
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.placeholder}>{props.placeholder}</div>
        )}
        <div className={styles.iconBox}>
          <p className={styles.icon}>▲</p>
        </div>
      </div>
      {isOpen && (
        <ScreenWatcher
          renderChildren={({ height }) => (
            <LayoutWatcher
              renderChildren={({ top }) => {
                const maxHeight = height - top - 60;

                return (
                  <ul
                    className={styles.list}
                    style={{
                      maxHeight: `${
                        !props.up && maxHeight < 320 && maxHeight > 100
                          ? maxHeight
                          : 320
                      }px`,
                    }}
                  >
                    {props.options.map((option) => (
                      <li
                        key={option.id}
                        className={classNames(styles.item, {
                          [styles.selectedListItem]: isOptionSelected(option),
                        })}
                        onClick={dispatchChange(option)}
                      >
                        {option.name}
                        {props.multiple && <span className={styles.checkbox} />}
                      </li>
                    ))}
                  </ul>
                );
              }}
            />
          )}
        />
      )}
    </div>
  );
}

export { SelectList };
